import React from 'react';
import { Link } from 'react-router-dom';

import type {
  WebsiteBreakdownBucket,
  WebsiteBreakdownCounts,
  WebsiteBreakdownResponse,
  WebsiteBreakdownStatus,
  WebsiteDestinationBreakdownRow,
  WebsiteDestinationPageCompletenessRow,
  WebsiteLandingPageCompletenessRow,
  WebsitePackageCompletenessRow,
} from '@utazzo/shared';

import { hiddenFilterValue } from '@/components/data-grid';
import {
  Card,
  Chip,
  Num,
  SectionHeading,
  SubTableHead,
} from '@/features/website-dashboard/components/ui';
import {
  fmt,
  isPageComplete,
  RULES,
  sellsWithoutLivePage,
  shortSections,
  statusLabel,
  statusTone,
  TEAL,
  TONE,
} from '@/features/website-dashboard/lib';

/**
 * The stacked "Detailed breakdown" - every number from
 * `/dashboard/website-breakdown`, laid out as plain spreadsheet-style tables
 * so a count can be looked up and opened in the list behind it. Table cell
 * styling comes from the scoped `.wd-root` CSS on the page wrapper.
 */

const LIVE_STATUS = { packages: 'ACTIVE', website: 'PUBLISHED' } as const;

/** A list URL with DataGrid filters applied (`f.<id>` is the grid convention). */
const listUrl = (path: string, filters: Record<string, string | undefined> = {}): string => {
  const params = new URLSearchParams();
  for (const [id, value] of Object.entries(filters)) if (value) params.set(`f.${id}`, value);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

const CardHeader = ({
  title,
  children,
  right,
  id,
}: {
  title: string;
  children?: React.ReactNode;
  right?: React.ReactNode;
  id?: string;
}): React.JSX.Element => (
  <div
    id={id}
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      padding: '14px 16px',
      borderBottom: '1px solid #eef0f0',
    }}
  >
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
      {children && <div style={{ fontSize: 12, color: '#6b7075' }}>{children}</div>}
    </div>
    {right}
  </div>
);

const SearchBox = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}): React.JSX.Element => (
  <input
    type="search"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    aria-label={placeholder.replace(/…$/, '')}
    style={{
      height: 32,
      padding: '0 10px',
      border: '1px solid #e3e5e5',
      borderRadius: 8,
      background: '#fff',
      fontSize: 12,
      minWidth: 200,
      outline: 'none',
    }}
  />
);

const Pill = ({
  active,
  onClick,
  count,
  children,
}: {
  active: boolean;
  onClick: () => void;
  count?: number;
  children: React.ReactNode;
}): React.JSX.Element => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      fontWeight: active ? 700 : 600,
      padding: '6px 10px',
      borderRadius: 8,
      background: active ? '#e7eef1' : '#fff',
      color: active ? TEAL : '#55595e',
      border: active ? '1px solid #c9d8de' : '1px solid #e3e5e5',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
    {count !== undefined && (
      <b
        style={{
          fontSize: 11,
          background: active ? '#fff' : '#f3f4f4',
          borderRadius: 6,
          padding: '0 6px',
        }}
      >
        {fmt(count)}
      </b>
    )}
  </button>
);

/** "461 packages · Published 310 · Draft 139" - every status that exists. */
const StatusSummary = ({
  label,
  statuses,
  path,
}: {
  label: string;
  statuses: WebsiteBreakdownStatus[];
  path: string;
}): React.JSX.Element => {
  const total = statuses.reduce((s, r) => s + r.count, 0);
  return (
    <>
      <b style={{ color: '#17171a' }}>
        {fmt(total)} {label}
      </b>
      {statuses.map((s) => (
        <span key={s.status}>
          {' · '}
          {statusLabel(s.status)}{' '}
          <Num value={s.count} strong to={listUrl(path, { status: s.status })} />
        </span>
      ))}
    </>
  );
};

// --- Destinations -----------------------------------------------------------

type DestFilter = 'all' | 'international' | 'domestic' | 'no_page';

const destinationFilterValue = (row: WebsiteDestinationBreakdownRow): string =>
  row.destinationId === null
    ? hiddenFilterValue('none', 'No destination set')
    : hiddenFilterValue(row.destinationId, row.name);

const DestinationPageChip = ({
  row,
}: {
  row: WebsiteDestinationBreakdownRow;
}): React.JSX.Element => {
  const pages = row.destinationPages;
  if (row.destinationId === null) return <Num value={pages.total} />;
  if (pages.total === 0)
    return (
      <Chip bg={TONE.bad.bg} fg={TONE.bad.fg}>
        No page
      </Chip>
    );
  const extra = pages.total > 1 ? ` (${pages.total})` : '';
  if (pages.live > 0) return <Chip bg={TONE.good.bg} fg={TONE.good.fg}>{`Live${extra}`}</Chip>;
  if (pages.draft > 0) return <Chip bg={TONE.muted.bg} fg={TONE.muted.fg}>{`Draft${extra}`}</Chip>;
  return <Chip bg={TONE.warn.bg} fg={TONE.warn.fg}>{`Not live${extra}`}</Chip>;
};

const DestinationsCard = ({
  rows,
}: {
  rows: WebsiteDestinationBreakdownRow[];
}): React.JSX.Element => {
  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState<DestFilter>('all');
  const noPageCount = rows.filter(
    (r) => r.destinationId !== null && sellsWithoutLivePage(r),
  ).length;

  const term = search.trim().toLowerCase();
  const visible = rows.filter((row) => {
    if (term && !row.name.toLowerCase().includes(term)) return false;
    if (filter === 'all') return true;
    if (filter === 'no_page') return row.destinationId !== null && sellsWithoutLivePage(row);
    if (row.isInternational === null) return false;
    return row.isInternational === (filter === 'international');
  });
  const sum = (pick: (r: WebsiteDestinationBreakdownRow) => number): number =>
    visible.reduce((s, r) => s + pick(r), 0);

  const filters: { key: DestFilter; label: string; count?: number }[] = [
    { key: 'all', label: 'All' },
    { key: 'international', label: 'International' },
    { key: 'domestic', label: 'Domestic' },
    { key: 'no_page', label: 'No live page', count: noPageCount },
  ];

  return (
    <Card id="wd-destinations">
      <CardHeader
        title="Destinations"
        right={
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <SearchBox value={search} onChange={setSearch} placeholder="Search destination…" />
            {filters.map((f) => (
              <Pill
                key={f.key}
                active={filter === f.key}
                onClick={() => setFilter(f.key)}
                count={f.count}
              >
                {f.label}
              </Pill>
            ))}
          </div>
        }
      >
        Packages, destination page and blogs per destination · busiest first
      </CardHeader>
      <div style={{ maxHeight: 420, overflow: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Destination</th>
              <th>Packages</th>
              <th>Published</th>
              <th>Draft</th>
              <th>Page</th>
              <th>Blogs</th>
              <th>Live blogs</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const destination = destinationFilterValue(row);
              const unlinked = row.destinationId === null;
              return (
                <tr key={row.destinationId ?? 'unlinked'}>
                  <td>
                    <span
                      style={{
                        color: unlinked ? '#6b7075' : '#17171a',
                        fontStyle: unlinked ? 'italic' : 'normal',
                      }}
                    >
                      {unlinked ? 'No destination set' : row.name}
                    </span>
                    {!unlinked && (
                      <span
                        style={{ fontSize: 10, fontWeight: 600, color: '#8a9095', marginLeft: 6 }}
                      >
                        {row.isInternational ? 'Intl' : 'Dom'}
                      </span>
                    )}
                  </td>
                  <td>
                    <Num
                      value={row.packages.total}
                      strong
                      to={listUrl('/packages', { destination })}
                    />
                  </td>
                  <td>
                    <Num
                      value={row.packages.live}
                      to={listUrl('/packages', { destination, status: LIVE_STATUS.packages })}
                    />
                  </td>
                  <td>
                    <Num
                      value={row.packages.draft}
                      to={listUrl('/packages', { destination, status: 'DRAFT' })}
                    />
                  </td>
                  <td>
                    <Link
                      to={listUrl('/destination-pages', { destination })}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block' }}
                    >
                      <DestinationPageChip row={row} />
                    </Link>
                  </td>
                  <td>
                    <Num value={row.blogs.total} to={listUrl('/blogs', { destination })} />
                  </td>
                  <td>
                    <Num
                      value={row.blogs.live}
                      to={listUrl('/blogs', { destination, status: LIVE_STATUS.website })}
                    />
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={7} style={{ color: '#8a9095', padding: '28px 12px' }}>
                  No destination matches.
                </td>
              </tr>
            )}
          </tbody>
          {visible.length > 1 && (
            <tfoot>
              <tr>
                <td>Total · {fmt(visible.length)} destinations</td>
                <td>{fmt(sum((r) => r.packages.total))}</td>
                <td>{fmt(sum((r) => r.packages.live))}</td>
                <td>{fmt(sum((r) => r.packages.draft))}</td>
                <td>
                  {fmt(sum((r) => r.destinationPages.total))}{' '}
                  <span style={{ fontSize: 10, fontWeight: 500, color: '#6b7075' }}>
                    ({fmt(sum((r) => r.destinationPages.live))} live +{' '}
                    {fmt(sum((r) => r.destinationPages.draft))} draft)
                  </span>
                </td>
                <td>{fmt(sum((r) => r.blogs.total))}</td>
                <td>{fmt(sum((r) => r.blogs.live))}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
};

// --- Packages ---------------------------------------------------------------

const other = (r: WebsiteBreakdownCounts): number => Math.max(0, r.total - r.live - r.draft);

const BucketTable = ({
  title,
  nameHeader,
  liveHeader,
  rows,
  footerLabel,
  footer,
  linkFor,
}: {
  title: string;
  nameHeader: string;
  liveHeader: string;
  rows: WebsiteBreakdownBucket[];
  footerLabel: string;
  footer: WebsiteBreakdownCounts;
  linkFor: (row: WebsiteBreakdownBucket | null, status?: 'live' | 'draft') => string;
}): React.JSX.Element => {
  const hasOther = rows.some((r) => other(r) > 0);
  return (
    <div style={{ border: '1px solid #e3e5e5', borderRadius: 12, overflow: 'hidden' }}>
      <SubTableHead>{title}</SubTableHead>
      <div style={{ maxHeight: 300, overflow: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>{nameHeader}</th>
              <th>Total</th>
              <th>{liveHeader}</th>
              <th>Draft</th>
              {hasOther && <th>Other</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td>
                  <Num value={row.total} strong to={linkFor(row)} />
                </td>
                <td>
                  <Num value={row.live} to={linkFor(row, 'live')} />
                </td>
                <td>
                  <Num value={row.draft} to={linkFor(row, 'draft')} />
                </td>
                {hasOther && <td>{fmt(other(row))}</td>}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>{footerLabel}</td>
              <td>{fmt(footer.total)}</td>
              <td>{fmt(footer.live)}</td>
              <td>{fmt(footer.draft)}</td>
              {hasOther && <td>{fmt(other(footer))}</td>}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const packageTotals = (data: WebsiteBreakdownResponse): WebsiteBreakdownCounts =>
  data.packages.byScope.reduce(
    (s, b) => ({ total: s.total + b.total, live: s.live + b.live, draft: s.draft + b.draft }),
    { total: 0, live: 0, draft: 0 },
  );

const splitLinks =
  (
    path: string,
    liveStatus: string,
    filterOf: (row: WebsiteBreakdownBucket) => Record<string, string>,
  ) =>
  (row: WebsiteBreakdownBucket | null, status?: 'live' | 'draft'): string =>
    listUrl(path, {
      ...(row ? filterOf(row) : {}),
      status: status === 'live' ? liveStatus : status === 'draft' ? 'DRAFT' : undefined,
    });

const PackagesCard = ({ data }: { data: WebsiteBreakdownResponse }): React.JSX.Element => {
  const totals = packageTotals(data);
  return (
    <Card id="wd-packages">
      <CardHeader
        title="Packages"
        right={
          <span style={{ fontSize: 11, color: '#8a9095' }}>
            By category: a package in 2 categories is counted in both rows
          </span>
        }
      >
        <StatusSummary label="packages" statuses={data.packages.byStatus} path="/packages" />
      </CardHeader>
      <div
        className="wd-three"
        style={{ display: 'grid', gap: 12, padding: '14px 16px', alignItems: 'start' }}
      >
        <BucketTable
          title="Domestic / International"
          nameHeader="Type"
          liveHeader="Published"
          rows={data.packages.byScope}
          footerLabel="Total"
          footer={totals}
          linkFor={splitLinks('/packages', LIVE_STATUS.packages, (row) => ({ scope: row.key }))}
        />
        <BucketTable
          title="By category"
          nameHeader="Category"
          liveHeader="Published"
          rows={data.packages.byCategory}
          footerLabel="Total packages"
          footer={totals}
          linkFor={splitLinks('/packages', LIVE_STATUS.packages, (row) => ({
            category: hiddenFilterValue(row.key, row.label),
          }))}
        />
        <BucketTable
          title="By trip type"
          nameHeader="Trip type"
          liveHeader="Published"
          rows={data.packages.byTripType}
          footerLabel="Total"
          footer={totals}
          linkFor={splitLinks('/packages', LIVE_STATUS.packages, (row) => ({
            tripType: hiddenFilterValue(row.key, row.label),
          }))}
        />
      </div>
    </Card>
  );
};

// --- Pages ------------------------------------------------------------------

const PagesCard = ({ data }: { data: WebsiteBreakdownResponse }): React.JSX.Element => {
  const dpTotals = data.destinationPages.byScope.reduce(
    (s, b) => ({ total: s.total + b.total, live: s.live + b.live, draft: s.draft + b.draft }),
    { total: 0, live: 0, draft: 0 },
  );
  return (
    <Card id="wd-pages">
      <CardHeader title="Destination & category pages">
        <StatusSummary
          label="destination pages"
          statuses={data.destinationPages.byStatus}
          path="/destination-pages"
        />
        {'  ·  '}
        <StatusSummary
          label="category pages"
          statuses={data.categoryPages.byStatus}
          path="/category-pages"
        />
      </CardHeader>
      <div
        className="wd-pages"
        style={{ display: 'grid', gap: 12, padding: '14px 16px', alignItems: 'start' }}
      >
        <BucketTable
          title="Destination pages by scope"
          nameHeader="Type"
          liveHeader="Live"
          rows={data.destinationPages.byScope}
          footerLabel="Total"
          footer={dpTotals}
          linkFor={splitLinks('/destination-pages', LIVE_STATUS.website, (row) => ({
            scope: row.key,
          }))}
        />
        <div style={{ border: '1px solid #e3e5e5', borderRadius: 12, overflow: 'hidden' }}>
          <SubTableHead>Category pages</SubTableHead>
          <div style={{ maxHeight: 300, overflow: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Category page</th>
                  <th>Status</th>
                  <th>Destinations listed</th>
                  <th>Best sellers</th>
                  <th>Health</th>
                </tr>
              </thead>
              <tbody>
                {data.categoryPages.rows.map((row) => {
                  const tone = statusTone(row.status);
                  const published = row.status === 'PUBLISHED' || row.status === 'ACTIVE';
                  const thin =
                    published &&
                    (row.destinationPages < RULES.minSections ||
                      row.bestSellers < RULES.minSections);
                  return (
                    <tr key={row.id}>
                      <td>
                        <Link
                          to={`/category-pages/${row.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {row.title}
                        </Link>
                      </td>
                      <td>
                        <Chip bg={tone.bg} fg={tone.fg}>
                          {statusLabel(row.status)}
                        </Chip>
                      </td>
                      <td>
                        <Num value={row.destinationPages} />
                      </td>
                      <td>
                        <Num value={row.bestSellers} />
                      </td>
                      <td>
                        <span style={{ fontSize: 11, color: thin ? '#b45309' : '#047857' }}>
                          {thin
                            ? `Thin - under ${RULES.minSections} ${row.destinationPages < RULES.minSections ? 'destinations' : 'best sellers'}`
                            : 'OK'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {data.categoryPages.rows.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ color: '#8a9095', padding: '28px 12px' }}>
                      No category pages yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Card>
  );
};

// --- Completeness -----------------------------------------------------------

type CompFilter = 'all' | 'incomplete' | 'complete' | 'banner' | 'sections' | 'cards';

const COMP_FILTERS: {
  key: CompFilter;
  label: string;
  missing?: boolean;
  keep: (r: WebsiteDestinationPageCompletenessRow) => boolean;
}[] = [
  { key: 'all', label: 'All', keep: () => true },
  { key: 'incomplete', label: 'Incomplete', keep: (r) => !isPageComplete(r) },
  { key: 'complete', label: 'Complete', keep: isPageComplete },
  { key: 'banner', label: 'Banner', missing: true, keep: (r) => !r.hasBanner },
  {
    key: 'sections',
    label: `Collection under ${RULES.minSections}`,
    missing: true,
    keep: (r) => r.sections.length < RULES.minSections,
  },
  {
    key: 'cards',
    label: `Collection under ${RULES.minCardsPerSection} cards`,
    missing: true,
    keep: (r) => shortSections(r).length > 0,
  },
];

const Target = ({ value, min }: { value: number; min: number }): React.JSX.Element => (
  <span
    style={{ color: value >= min ? '#047857' : '#b45309', fontWeight: value >= min ? 500 : 700 }}
  >
    {fmt(value)} <span style={{ color: '#a0a5a9', fontWeight: 400 }}>/ {min}</span>
  </span>
);

const CompletenessCard = ({
  rows,
}: {
  rows: WebsiteDestinationPageCompletenessRow[];
}): React.JSX.Element => {
  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState<CompFilter>('incomplete');
  const term = search.trim().toLowerCase();
  const keep = COMP_FILTERS.find((f) => f.key === filter)?.keep ?? (() => true);
  const visible = rows.filter((r) => {
    if (term && !r.title.toLowerCase().includes(term)) return false;
    return keep(r);
  });

  return (
    <Card id="wd-completeness">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: '14px 16px',
          borderBottom: '1px solid #eef0f0',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Destination page completeness</div>
            <div style={{ fontSize: 12, color: '#6b7075' }}>
              A page is complete with a banner, at least {RULES.minSections} collections, and{' '}
              {RULES.minCardsPerSection}+ cards in every collection. Reels are shown for reference
              but do not affect completeness.
            </div>
          </div>
          <SearchBox value={search} onChange={setSearch} placeholder="Search page…" />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {COMP_FILTERS.filter((f) => !f.missing).map((f) => (
            <Pill
              key={f.key}
              active={filter === f.key}
              onClick={() => setFilter(f.key)}
              count={rows.filter(f.keep).length}
            >
              {f.label}
            </Pill>
          ))}
          <span style={{ width: 1, height: 18, background: '#e3e5e5', margin: '0 4px' }} />
          <span style={{ fontSize: 12, color: '#6b7075' }}>Missing:</span>
          {COMP_FILTERS.filter((f) => f.missing).map((f) => (
            <Pill
              key={f.key}
              active={filter === f.key}
              onClick={() => setFilter(f.key)}
              count={rows.filter(f.keep).length}
            >
              {f.label}
            </Pill>
          ))}
        </div>
      </div>
      <div style={{ maxHeight: 440, overflow: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Destination page</th>
              <th>Status</th>
              <th>Banner</th>
              <th>Reels</th>
              <th>Collection</th>
              <th>Collection under {RULES.minCardsPerSection} cards</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const tone = statusTone(row.status);
              const complete = isPageComplete(row);
              const short = shortSections(row);
              return (
                <tr key={row.id}>
                  <td>
                    <Link
                      to={`/destination-pages/${row.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {row.title}
                    </Link>
                  </td>
                  <td>
                    <Chip bg={tone.bg} fg={tone.fg}>
                      {statusLabel(row.status)}
                    </Chip>
                  </td>
                  <td>
                    {row.hasBanner ? (
                      <Chip bg={TONE.good.bg} fg={TONE.good.fg}>
                        Yes
                      </Chip>
                    ) : (
                      <Chip bg={TONE.warn.bg} fg={TONE.warn.fg}>
                        Missing
                      </Chip>
                    )}
                  </td>
                  <td>
                    <span style={{ color: '#6b7075', fontWeight: 500 }}>{fmt(row.reels)}</span>
                  </td>
                  <td>
                    {row.sectionsSource === 'none' ? (
                      <Chip bg={TONE.warn.bg} fg={TONE.warn.fg}>
                        No destination linked
                      </Chip>
                    ) : (
                      <Target value={row.sections.length} min={RULES.minSections} />
                    )}
                  </td>
                  <td>
                    {short.length === 0 ? (
                      <span style={{ color: '#c5c9cb' }}>-</span>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'center',
                          gap: 4,
                        }}
                      >
                        {short.map((s, i) => (
                          <Chip key={`${s.heading}-${i}`} bg={TONE.warn.bg} fg={TONE.warn.fg}>
                            {s.heading} ({fmt(s.cards)})
                          </Chip>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    {complete ? (
                      <Chip bg={TONE.good.bg} fg={TONE.good.fg}>
                        Complete
                      </Chip>
                    ) : (
                      <Chip bg={TONE.warn.bg} fg={TONE.warn.fg}>
                        Incomplete
                      </Chip>
                    )}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={7} style={{ color: '#8a9095', padding: '28px 12px' }}>
                  No page matches.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// --- Package completeness ---------------------------------------------------

// minImages counts main + banner images (0-2); the gallery table is almost
// never filled, so >=1 (has a main or banner image) is the completeness bar.
const PKG_RULES = { minImages: 1, minInclusions: 1 } as const;

const isPkgComplete = (r: WebsitePackageCompletenessRow): boolean =>
  r.hasBanner &&
  r.images >= PKG_RULES.minImages &&
  r.hasOverview &&
  r.itineraryDays >= r.numberOfDays &&
  r.inclusions >= PKG_RULES.minInclusions &&
  r.hasSeo;

type PkgCompFilter =
  | 'all'
  | 'incomplete'
  | 'complete'
  | 'banner'
  | 'images'
  | 'overview'
  | 'itinerary'
  | 'inclusions'
  | 'seo';

const PKG_COMP_FILTERS: {
  key: PkgCompFilter;
  label: string;
  missing?: boolean;
  keep: (r: WebsitePackageCompletenessRow) => boolean;
}[] = [
  { key: 'all', label: 'All', keep: () => true },
  { key: 'incomplete', label: 'Incomplete', keep: (r) => !isPkgComplete(r) },
  { key: 'complete', label: 'Complete', keep: isPkgComplete },
  { key: 'banner', label: 'Banner', missing: true, keep: (r) => !r.hasBanner },
  {
    key: 'images',
    label: `Images under ${PKG_RULES.minImages}`,
    missing: true,
    keep: (r) => r.images < PKG_RULES.minImages,
  },
  { key: 'overview', label: 'Overview', missing: true, keep: (r) => !r.hasOverview },
  {
    key: 'itinerary',
    label: 'Itinerary',
    missing: true,
    keep: (r) => r.itineraryDays < r.numberOfDays,
  },
  {
    key: 'inclusions',
    label: 'Inclusions',
    missing: true,
    keep: (r) => r.inclusions < PKG_RULES.minInclusions,
  },
  { key: 'seo', label: 'SEO', missing: true, keep: (r) => !r.hasSeo },
];

const PackageCompletenessCard = ({
  rows,
}: {
  rows: WebsitePackageCompletenessRow[];
}): React.JSX.Element => {
  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState<PkgCompFilter>('incomplete');
  const term = search.trim().toLowerCase();
  const keep = PKG_COMP_FILTERS.find((f) => f.key === filter)?.keep ?? (() => true);
  const visible = rows.filter((r) => {
    if (term && !r.title.toLowerCase().includes(term)) return false;
    return keep(r);
  });
  const yesNo = (ok: boolean) =>
    ok ? (
      <Chip bg={TONE.good.bg} fg={TONE.good.fg}>
        Yes
      </Chip>
    ) : (
      <Chip bg={TONE.warn.bg} fg={TONE.warn.fg}>
        Missing
      </Chip>
    );

  return (
    <Card id="wd-package-completeness">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: '14px 16px',
          borderBottom: '1px solid #eef0f0',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Package completeness</div>
            <div style={{ fontSize: 12, color: '#6b7075' }}>
              A package is complete with a banner / main image, a main or banner image (Images
              counts those two), an overview, a day for every day of the itinerary, at least{' '}
              {PKG_RULES.minInclusions} inclusion, and SEO meta (title + description).
            </div>
          </div>
          <SearchBox value={search} onChange={setSearch} placeholder="Search package…" />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {PKG_COMP_FILTERS.filter((f) => !f.missing).map((f) => (
            <Pill
              key={f.key}
              active={filter === f.key}
              onClick={() => setFilter(f.key)}
              count={rows.filter(f.keep).length}
            >
              {f.label}
            </Pill>
          ))}
          <span style={{ width: 1, height: 18, background: '#e3e5e5', margin: '0 4px' }} />
          <span style={{ fontSize: 12, color: '#6b7075' }}>Missing:</span>
          {PKG_COMP_FILTERS.filter((f) => f.missing).map((f) => (
            <Pill
              key={f.key}
              active={filter === f.key}
              onClick={() => setFilter(f.key)}
              count={rows.filter(f.keep).length}
            >
              {f.label}
            </Pill>
          ))}
        </div>
      </div>
      <div style={{ maxHeight: 440, overflow: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Package</th>
              <th>Status</th>
              <th>Banner</th>
              <th>Images</th>
              <th>Overview</th>
              <th>Itinerary</th>
              <th>Inclusions</th>
              <th>SEO</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const tone = statusTone(row.status);
              return (
                <tr key={row.id}>
                  <td>
                    <Link to={`/packages/${row.id}`} target="_blank" rel="noopener noreferrer">
                      {row.title}
                    </Link>
                  </td>
                  <td>
                    <Chip bg={tone.bg} fg={tone.fg}>
                      {statusLabel(row.status)}
                    </Chip>
                  </td>
                  <td>{yesNo(row.hasBanner)}</td>
                  <td>
                    <Target value={row.images} min={PKG_RULES.minImages} />
                  </td>
                  <td>{yesNo(row.hasOverview)}</td>
                  <td>
                    <Target value={row.itineraryDays} min={row.numberOfDays} />
                  </td>
                  <td>
                    <Target value={row.inclusions} min={PKG_RULES.minInclusions} />
                  </td>
                  <td>{yesNo(row.hasSeo)}</td>
                  <td>
                    {isPkgComplete(row) ? (
                      <Chip bg={TONE.good.bg} fg={TONE.good.fg}>
                        Complete
                      </Chip>
                    ) : (
                      <Chip bg={TONE.warn.bg} fg={TONE.warn.fg}>
                        Incomplete
                      </Chip>
                    )}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={9} style={{ color: '#8a9095', padding: '28px 12px' }}>
                  No package matches.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// --- Landing page completeness ----------------------------------------------

const LP_RULES = { minPackages: 3, minReviews: 3 } as const;

const isLpComplete = (r: WebsiteLandingPageCompletenessRow): boolean =>
  r.hasBanner && r.packages >= LP_RULES.minPackages && r.reviews >= LP_RULES.minReviews;

type LpCompFilter = 'all' | 'incomplete' | 'complete' | 'banner' | 'packages' | 'reviews';

const LP_COMP_FILTERS: {
  key: LpCompFilter;
  label: string;
  missing?: boolean;
  keep: (r: WebsiteLandingPageCompletenessRow) => boolean;
}[] = [
  { key: 'all', label: 'All', keep: () => true },
  { key: 'incomplete', label: 'Incomplete', keep: (r) => !isLpComplete(r) },
  { key: 'complete', label: 'Complete', keep: isLpComplete },
  { key: 'banner', label: 'Banner', missing: true, keep: (r) => !r.hasBanner },
  {
    key: 'packages',
    label: `Packages under ${LP_RULES.minPackages}`,
    missing: true,
    keep: (r) => r.packages < LP_RULES.minPackages,
  },
  {
    key: 'reviews',
    label: `Reviews under ${LP_RULES.minReviews}`,
    missing: true,
    keep: (r) => r.reviews < LP_RULES.minReviews,
  },
];

const LandingPageCompletenessCard = ({
  rows,
}: {
  rows: WebsiteLandingPageCompletenessRow[];
}): React.JSX.Element => {
  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState<LpCompFilter>('incomplete');
  const term = search.trim().toLowerCase();
  const keep = LP_COMP_FILTERS.find((f) => f.key === filter)?.keep ?? (() => true);
  const visible = rows.filter((r) => {
    if (term && !r.title.toLowerCase().includes(term)) return false;
    return keep(r);
  });

  return (
    <Card id="wd-landing-completeness">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: '14px 16px',
          borderBottom: '1px solid #eef0f0',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Landing page completeness</div>
            <div style={{ fontSize: 12, color: '#6b7075' }}>
              A landing page is complete with a banner, at least {LP_RULES.minPackages} custom
              packages, and at least {LP_RULES.minReviews} reviews (client stories).
            </div>
          </div>
          <SearchBox value={search} onChange={setSearch} placeholder="Search landing page…" />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {LP_COMP_FILTERS.filter((f) => !f.missing).map((f) => (
            <Pill
              key={f.key}
              active={filter === f.key}
              onClick={() => setFilter(f.key)}
              count={rows.filter(f.keep).length}
            >
              {f.label}
            </Pill>
          ))}
          <span style={{ width: 1, height: 18, background: '#e3e5e5', margin: '0 4px' }} />
          <span style={{ fontSize: 12, color: '#6b7075' }}>Missing:</span>
          {LP_COMP_FILTERS.filter((f) => f.missing).map((f) => (
            <Pill
              key={f.key}
              active={filter === f.key}
              onClick={() => setFilter(f.key)}
              count={rows.filter(f.keep).length}
            >
              {f.label}
            </Pill>
          ))}
        </div>
      </div>
      <div style={{ maxHeight: 440, overflow: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Landing page</th>
              <th>Status</th>
              <th>Banner</th>
              <th>Packages</th>
              <th>Reviews</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const tone = statusTone(row.status);
              return (
                <tr key={row.id}>
                  <td>
                    <Link to={`/landing-pages/${row.id}`} target="_blank" rel="noopener noreferrer">
                      {row.title}
                    </Link>
                  </td>
                  <td>
                    <Chip bg={tone.bg} fg={tone.fg}>
                      {statusLabel(row.status)}
                    </Chip>
                  </td>
                  <td>
                    {row.hasBanner ? (
                      <Chip bg={TONE.good.bg} fg={TONE.good.fg}>
                        Yes
                      </Chip>
                    ) : (
                      <Chip bg={TONE.warn.bg} fg={TONE.warn.fg}>
                        Missing
                      </Chip>
                    )}
                  </td>
                  <td>
                    <Target value={row.packages} min={LP_RULES.minPackages} />
                  </td>
                  <td>
                    <Target value={row.reviews} min={LP_RULES.minReviews} />
                  </td>
                  <td>
                    {isLpComplete(row) ? (
                      <Chip bg={TONE.good.bg} fg={TONE.good.fg}>
                        Complete
                      </Chip>
                    ) : (
                      <Chip bg={TONE.warn.bg} fg={TONE.warn.fg}>
                        Incomplete
                      </Chip>
                    )}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={6} style={{ color: '#8a9095', padding: '28px 12px' }}>
                  No landing page matches.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// --- Blogs ------------------------------------------------------------------

const BlogsCard = ({ data }: { data: WebsiteBreakdownResponse }): React.JSX.Element => {
  const categorised = new Set(data.blogCategories.map((c) => c.id));
  const leftovers = data.blogs.byCategory.filter((b) => !categorised.has(b.key));
  const blogLink = (categoryId: string, name: string, status?: 'live' | 'draft'): string =>
    listUrl('/blogs', {
      category: hiddenFilterValue(categoryId, name),
      status: status === 'live' ? LIVE_STATUS.website : status === 'draft' ? 'DRAFT' : undefined,
    });

  const linkedDests = data.destinations.filter((d) => d.destinationId !== null);
  const withBlogs = linkedDests.filter((d) => d.blogs.total > 0).length;
  const sortedDests = [...data.destinations].sort((a, b) => {
    if (a.destinationId === null) return 1;
    if (b.destinationId === null) return -1;
    return b.blogs.total - a.blogs.total || a.name.localeCompare(b.name);
  });
  const blogFooter = data.destinations.reduce(
    (s, r) => ({
      total: s.total + r.blogs.total,
      live: s.live + r.blogs.live,
      draft: s.draft + r.blogs.draft,
    }),
    { total: 0, live: 0, draft: 0 },
  );

  return (
    <Card id="wd-blogs">
      <CardHeader
        title="Blogs"
        right={
          <span style={{ fontSize: 12, color: '#6b7075' }}>
            <b style={{ color: '#17171a', fontWeight: 700 }}>{fmt(withBlogs)}</b> of{' '}
            {fmt(linkedDests.length)} destinations have a blog
          </span>
        }
      >
        <StatusSummary label="blogs" statuses={data.blogs.byStatus} path="/blogs" />
      </CardHeader>
      <div
        className="wd-two"
        style={{ display: 'grid', gap: 12, padding: '14px 16px', alignItems: 'start' }}
      >
        <div style={{ border: '1px solid #e3e5e5', borderRadius: 12, overflow: 'hidden' }}>
          <SubTableHead>Blogs by category</SubTableHead>
          <div style={{ maxHeight: 320, overflow: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Blog category</th>
                  <th>Category is</th>
                  <th>Blogs</th>
                  <th>Live</th>
                  <th>Draft</th>
                </tr>
              </thead>
              <tbody>
                {data.blogCategories.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td>
                      <Chip
                        bg={row.isActive ? TONE.good.bg : TONE.muted.bg}
                        fg={row.isActive ? TONE.good.fg : TONE.muted.fg}
                      >
                        {row.isActive ? 'Published' : 'Unpublished'}
                      </Chip>
                    </td>
                    <td>
                      <Num value={row.posts.total} strong to={blogLink(row.id, row.name)} />
                    </td>
                    <td>
                      <Num value={row.posts.live} to={blogLink(row.id, row.name, 'live')} />
                    </td>
                    <td>
                      <Num value={row.posts.draft} to={blogLink(row.id, row.name, 'draft')} />
                    </td>
                  </tr>
                ))}
                {leftovers.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <span style={{ color: '#6b7075', fontStyle: 'italic' }}>{row.label}</span>
                    </td>
                    <td>
                      <span style={{ color: '#c5c9cb' }}>-</span>
                    </td>
                    <td>
                      <Num value={row.total} strong to={blogLink(row.key, row.label)} />
                    </td>
                    <td>
                      <Num value={row.live} to={blogLink(row.key, row.label, 'live')} />
                    </td>
                    <td>
                      <Num value={row.draft} to={blogLink(row.key, row.label, 'draft')} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ border: '1px solid #e3e5e5', borderRadius: 12, overflow: 'hidden' }}>
          <SubTableHead>Blogs by destination</SubTableHead>
          <div style={{ maxHeight: 320, overflow: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Destination</th>
                  <th>Blogs</th>
                  <th>Live</th>
                  <th>Draft</th>
                </tr>
              </thead>
              <tbody>
                {sortedDests.map((row) => {
                  const unlinked = row.destinationId === null;
                  const destination = destinationFilterValue(row);
                  return (
                    <tr key={row.destinationId ?? 'none'}>
                      <td>
                        <span
                          style={{
                            color: unlinked ? '#6b7075' : '#17171a',
                            fontStyle: unlinked ? 'italic' : 'normal',
                          }}
                        >
                          {row.name}
                        </span>
                      </td>
                      <td>
                        <Num
                          value={row.blogs.total}
                          strong
                          to={listUrl('/blogs', { destination })}
                        />
                      </td>
                      <td>
                        <Num
                          value={row.blogs.live}
                          to={listUrl('/blogs', { destination, status: LIVE_STATUS.website })}
                        />
                      </td>
                      <td>
                        <Num
                          value={row.blogs.draft}
                          to={listUrl('/blogs', { destination, status: 'DRAFT' })}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td>{fmt(blogFooter.total)}</td>
                  <td>{fmt(blogFooter.live)}</td>
                  <td>{fmt(blogFooter.draft)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </Card>
  );
};

// --- Section shell ----------------------------------------------------------

const ANCHORS: { href: string; label: string }[] = [
  { href: '#wd-completeness', label: 'Page completeness' },
  { href: '#wd-package-completeness', label: 'Package completeness' },
  { href: '#wd-landing-completeness', label: 'Landing page completeness' },
  { href: '#wd-destinations', label: 'Destinations' },
  { href: '#wd-packages', label: 'Packages' },
  { href: '#wd-pages', label: 'Pages' },
  { href: '#wd-blogs', label: 'Blogs' },
];

export const BreakdownSection = ({
  data,
}: {
  data: WebsiteBreakdownResponse;
}): React.JSX.Element => (
  <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    <SectionHeading
      title="Detailed breakdown"
      right={
        <span style={{ fontSize: 11, color: '#6b7075', whiteSpace: 'nowrap' }}>
          Every number opens the list behind it
        </span>
      }
    />
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {ANCHORS.map((a) => (
        <a
          key={a.href}
          href={a.href}
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: '5px 10px',
            borderRadius: 999,
            background: '#fff',
            border: '1px solid #e3e5e5',
            color: '#2a2e32',
          }}
        >
          {a.label}
        </a>
      ))}
    </div>
    <CompletenessCard rows={data.destinationPageCompleteness} />
    <PackageCompletenessCard rows={data.packageCompleteness} />
    <LandingPageCompletenessCard rows={data.landingPageCompleteness} />
    <DestinationsCard rows={data.destinations} />
    <PackagesCard data={data} />
    <PagesCard data={data} />
    <BlogsCard data={data} />
  </section>
);
